import { Coins, Snowflake, Sun } from "lucide-react";
import { useState } from "react";
import Flag from "react-world-flags";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { useBalancesHidden } from "@/shared/hooks/useBalancesHidden";
import useTranslations from "@/shared/lang/useTranslations";

import { getColorForCurrency } from "../constants/walletColors.constants";
import { isCryptoCurrency } from "../constants/walletMethods.constants";
import {
  CURRENCY_TO_COUNTRY,
  CURRENCY_TO_DISPERSION_CODE,
  CURRENCY_TO_FLAG_CODE,
} from "../constants/wallets.constants";
import type { RuntimeWallet } from "../types/payments-wallets.types";
import { currencyFractionDigits, resolveLocale } from "../utils/formatWalletAmount";
import { BalanceVisibilityToggle } from "./BalanceVisibilityToggle";
import { FreezeWalletModal } from "./FreezeWalletModal";
import { UnfreezeWalletDialog } from "./UnfreezeWalletDialog";

interface WalletStatementHeroProps {
  wallet: RuntimeWallet;
  /** Called after a freeze/unfreeze succeeds so the caller can refetch the wallet. */
  onStatusChanged?: () => void;
}

/**
 * Compact header for the statement view: flag/country, available balance, status, pending, and (INTERNAL)
 * freeze/unfreeze.
 */
export const WalletStatementHero = ({ wallet, onStatusChanged }: WalletStatementHeroProps) => {
  const { t, activeLanguage } = useTranslations("wallets");
  const { isInternalUser } = useAuth();
  const { hidden: balancesHidden } = useBalancesHidden();
  const [isFreezeOpen, setIsFreezeOpen] = useState(false);
  const [isUnfreezeOpen, setIsUnfreezeOpen] = useState(false);

  const isCrypto = isCryptoCurrency(wallet.currency);
  const isActive = wallet.status === "active";
  const isFrozen = wallet.status === "frozen";
  const accentColor = getColorForCurrency(wallet.currency);
  const countryName = CURRENCY_TO_COUNTRY[wallet.currency] ?? "";
  const flagCode = !isCrypto
    ? (CURRENCY_TO_FLAG_CODE[wallet.currency] ?? CURRENCY_TO_DISPERSION_CODE[wallet.currency] ?? null)
    : null;
  const decimals = currencyFractionDigits(wallet.currency);
  const locale = resolveLocale(activeLanguage);
  const hasPending = Number(wallet.balance_pending) > 0;
  const hasBlocked = Number(wallet.balance_blocked) > 0;
  const statusLabel = isActive
    ? t("card_status_active")
    : isFrozen
      ? t("card_status_frozen")
      : t("card_status_closed");

  return (
    <section className="overflow-hidden rounded-lg border border-border bg-card p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3.5">
          <div
            className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border"
            style={{
              backgroundColor: `color-mix(in oklch, ${accentColor} 10%, transparent)`,
              borderColor: `color-mix(in oklch, ${accentColor} 24%, transparent)`,
            }}
            aria-hidden="true"
          >
            {isCrypto || !flagCode ? (
              <Coins size={18} style={{ color: accentColor }} />
            ) : (
              <Flag code={flagCode} className="h-5 w-7 rounded-[2px] object-cover" aria-hidden />
            )}
          </div>
          <div className="min-w-0">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h1 className="text-[19px] font-semibold tracking-tight text-foreground">
                {countryName || wallet.currency}
              </h1>
              <ColocaBadge
                variant={isActive ? "success" : isFrozen ? "warning" : "secondary"}
                className="text-[9.5px]"
              >
                <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
                {statusLabel}
              </ColocaBadge>
            </div>
            <div className="flex items-center gap-1">
              <ColocaAmountDisplay
                value={wallet.balance_available}
                animate
                masked={balancesHidden}
                currency={wallet.currency}
                minimumFractionDigits={decimals.min}
                maximumFractionDigits={decimals.max}
                locale={locale}
                size="xl"
                className="min-w-0 truncate text-foreground"
              />
              <BalanceVisibilityToggle />
            </div>
            <p className="mt-1.5 font-mono text-[10.5px] uppercase tracking-wider text-muted-foreground">
              {t("movements_hero_balance_label")}
            </p>
            {hasPending && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t("statement_pending_label")}:{" "}
                <ColocaAmountDisplay
                  value={wallet.balance_pending}
                  animate
                  masked={balancesHidden}
                  currency={wallet.currency}
                  minimumFractionDigits={decimals.min}
                  maximumFractionDigits={decimals.max}
                  locale={locale}
                  size="xs"
                />
              </p>
            )}
            {hasBlocked && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t("statement_blocked_label")}:{" "}
                <ColocaAmountDisplay
                  value={wallet.balance_blocked}
                  animate
                  masked={balancesHidden}
                  currency={wallet.currency}
                  minimumFractionDigits={decimals.min}
                  maximumFractionDigits={decimals.max}
                  locale={locale}
                  size="xs"
                />
              </p>
            )}
            {isFrozen && wallet.frozen_reason && (
              <p className="mt-1 text-xs text-warning">{wallet.frozen_reason}</p>
            )}
          </div>
        </div>

        {/* Freeze/unfreeze — INTERNAL staff only; the backend enforces payments:write (403 → toast). */}
        {isInternalUser && wallet.status !== "closed" && (
          <div className="self-start">
            {isActive ? (
              <ColocaButton
                variant="secondary"
                size="sm"
                icon={Snowflake}
                onClick={() => setIsFreezeOpen(true)}
              >
                {t("statement_freeze")}
              </ColocaButton>
            ) : (
              <ColocaButton variant="secondary" size="sm" icon={Sun} onClick={() => setIsUnfreezeOpen(true)}>
                {t("statement_unfreeze")}
              </ColocaButton>
            )}
          </div>
        )}
      </div>

      <FreezeWalletModal
        open={isFreezeOpen}
        onClose={() => setIsFreezeOpen(false)}
        wallet={wallet}
        onFrozen={() => onStatusChanged?.()}
      />
      <UnfreezeWalletDialog
        open={isUnfreezeOpen}
        onClose={() => setIsUnfreezeOpen(false)}
        wallet={wallet}
        onUnfrozen={() => onStatusChanged?.()}
      />
    </section>
  );
};
